import { AxiosInstance, AxiosRequestConfig } from 'axios'

import { baseService } from './base.service'
import {
  ChangePasswordDto,
  CreateUsersDto,
  SearchUserDto,
  UpdateProfile,
  UpdateUserDto,
} from './dto/user-dto.interface'
import { IBaseService } from './interfaces/base-service.interface'

import { QueryParams } from '@/types/base.type'
import { ListResponse } from '@/types/list.type'
import { UserDataType, UserEngagementStats, UserProfile } from '@/types/user.type'

export interface Discussion {
  id: string
  title: string
  createdAt: string
}

export interface Meme {
  id: string
  content: string
  createdAt: string
}

export interface PaginatedResponse<T> {
  data: T[]
  meta: {
    totalItems: number
    totalPages: number
    currentPage: number
    pageSize: number
  }
}
export class UserService {
  private static instance: UserService
  private readonly api: AxiosInstance

  private constructor(private readonly baseService: IBaseService) {
    this.api = this.baseService.getClient()
  }

  public static getInstance(baseService: IBaseService): UserService {
    if (!UserService.instance) {
      UserService.instance = new UserService(baseService)
    }
    return UserService.instance
  }

  getProfile = async (configs?: AxiosRequestConfig) => {
    const { data } = await this.api.get<UserProfile>('/user/profile', configs)
    return data
  }

  getUser = async (userAlias: string, configs?: AxiosRequestConfig) => {
    const { data } = await this.api.get<UserProfile>(`/user/${userAlias}`, configs)
    return data
  }

  createUser = async (createUserDto: CreateUsersDto) => {
    const data = await this.api.post<UserDataType>('/admin/users', createUserDto)
    return data
  }

  updateUser = async (id: number, userDto: UpdateUserDto) => {
    const { data } = await this.api.put<UserDataType>(`/admin/user/${id}`, userDto)
    return data
  }

  getUsers = async ({ queryParams, configs }: { queryParams: QueryParams; configs?: AxiosRequestConfig }) => {
    const { pageNumber, pageSize, sort, search, searchFields, roles, emails } = queryParams
    const params = new URLSearchParams()

    if (pageNumber) {
      params.set('pageNumber', String(pageNumber))
    }
    if (pageSize) {
      params.set('pageSize', String(pageSize))
    }
    if (sort) {
      params.set('sort', sort)
    }
    if (searchFields?.length) {
      params.set('searchFields', searchFields.join(','))
    }
    if (search) {
      params.set('search', search)
    }
    if (roles && roles.length > 0) {
      params.set('roles', roles.join(','))
    }
    if (emails) {
      params.set('emails', emails)
    }

    const query = params.toString()
    const url = query ? `/admin/users?${query}` : '/admin/users'
    const { data } = await this.api.get<ListResponse<UserDataType>>(url, configs)
    return data
  }

  getReferrers = async ({ queryParams, configs }: { queryParams: QueryParams; configs?: AxiosRequestConfig }) => {
    const { pageNumber, pageSize, sort, search, searchFields } = queryParams
    const params = new URLSearchParams()

    if (pageNumber) {
      params.set('pageNumber', String(pageNumber))
    }
    if (pageSize) {
      params.set('pageSize', String(pageSize))
    }
    if (sort) {
      params.set('sort', sort)
    }
    if (searchFields?.length) {
      params.set('searchFields', searchFields.join(','))
    }
    if (search) {
      params.set('search', search)
    }

    const query = params.toString()
    const url = query ? `/admin/referrers?${query}` : '/admin/referrers'
    const { data } = await this.api.get<ListResponse<UserDataType>>(url, configs)
    return data
  }

  getUserById = async <T>(id: number, configs?: AxiosRequestConfig) => {
    const { data } = await this.api.get<T>(`/admin/user/${id}`, configs)
    return data
  }

  deleteUser = async (id: number, configs?: AxiosRequestConfig) => {
    const { data } = await this.api.delete(`/admin/user/${id}`, configs)
    return data
  }

  updateProfile = async (userDto: UpdateProfile, configs?: AxiosRequestConfig) => {
    const { data } = await this.api.put<UserProfile>('/user/profile/edit', userDto, configs)
    return data
  }

  uploadProfileAvatar = async (file: File, configs?: AxiosRequestConfig) => {
    const formData = new FormData()
    formData.append('file', file)
    const { data } = await this.api.post<{ avatar: string }>('/user/profile/avatar', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      ...configs,
    })
    return data
  }

  changePassword = async (changePasswordDto: ChangePasswordDto, configs?: AxiosRequestConfig) => {
    const { data } = await this.api.put<{ success: boolean }>('/user/change-password', changePasswordDto, configs)
    return data
  }

  getTopUsersByEngagement = async (limit?: number, configs?: AxiosRequestConfig) => {
    const url = limit ? `/user/topic/top-engagement?limit=${limit}` : '/user/topic/top-engagement'
    const { data } = await this.api.get<UserEngagementStats[]>(url, configs)
    return data
  }

  getUserEngagementStats = async (configs?: AxiosRequestConfig) => {
    const { data } = await this.api.get<UserEngagementStats>('/user/topic/engagement-stats', configs)
    return data
  }

  getUserDiscussions = async (
    userId: number,
    queryParams: QueryParams,
    configs?: AxiosRequestConfig
  ): Promise<PaginatedResponse<Discussion>> => {
    const { data } = await this.api.get<PaginatedResponse<Discussion>>(`/user/${userId}/forum-topic`, {
      params: queryParams,
      ...configs,
    })
    return data
  }

  getUserMemes = async (
    userId: number,
    queryParams: QueryParams,
    configs?: AxiosRequestConfig
  ): Promise<PaginatedResponse<Meme>> => {
    const { pageNumber = 1, pageSize = 10 } = queryParams
    const url = `user/${userId}/meme-topic?pageNumber=${pageNumber}&pageSize=${pageSize}`

    const { data } = await this.api.get<PaginatedResponse<Meme>>(url, configs)
    return data
  }

  searchUsers = async (searchUserDto: SearchUserDto) => {
    const { data: dataResponse } = await this.api.get<ListResponse<UserProfile>>('/user/search', {
      params: searchUserDto,
    })
    return dataResponse.data
  }
}

const userService = UserService.getInstance(baseService)

export default userService
